import { useParams } from "react-router-dom";
import Seo, { breadcrumbLd, faqLd } from "../lib/seo";
import { SITE } from "../lib/site";
import { PRODUCTS, INDUSTRIES } from "../data/content";
import { Breadcrumbs } from "../components/site/Layout";
import { CtaBand, RelatedGrid } from "../components/site/Sections";
import { Reveal, Overline } from "../components/site/Reveal";
import NotFound from "./NotFound";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";

const productFaqs = (p) => [
  { q: `Can you customize ${p.name.toLowerCase()} with our logo?`, a: `Yes. ${p.name} are produced around your artwork, logo and security requirements with custom sizes and finishes.` },
  { q: `Do ${p.name.toLowerCase()} support serialisation?`, a: `Serial numbers, barcodes and QR codes can be added to support traceability and authentication where required.` },
  { q: `Can businesses across the UAE order ${p.name.toLowerCase()}?`, a: `Yes. We are based in Sharjah Media City and supply ${p.name.toLowerCase()} to businesses across Dubai, Abu Dhabi and the wider UAE.` },
];

function Block({ label, items }) {
  return (
    <div className="card p-6">
      <Overline>{label}</Overline>
      <ul className="mt-4 space-y-2">
        {items.map((it, i) => (
          <li key={i} className="text-sm text-[#C9C9D4] flex gap-3"><span className="text-holo-cyan">—</span>{it}</li>
        ))}
      </ul>
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const p = PRODUCTS.find((x) => x.slug === slug);
  if (!p) return <NotFound />;
  const path = `/products/${p.slug}`;
  const faqs = productFaqs(p);

  return (
    <>
      <Seo title={`${p.h1} | NanoMax Holo Solutions`} description={`${p.intro.slice(0, 155)}`} path={path} image={p.image} type="article"
        jsonLd={[
          { "@context": "https://schema.org", "@type": "Product", name: p.name, image: p.image, description: p.tile, brand: { "@type": "Brand", name: SITE.legal }, manufacturer: { "@type": "Organization", name: SITE.legal } },
          breadcrumbLd([{ name: "Home", path: "/" }, { name: "Products", path: "/products" }, { name: p.name, path }]),
          faqLd(faqs),
        ]} />
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Products", path: "/products" }, { name: p.name }]} />

      <section className="px-6 md:px-10 lg:px-16 pt-8 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <Overline>Product</Overline>
          <h1 className="font-head font-black uppercase tracking-tighter leading-[0.95] text-4xl md:text-5xl mt-5">{p.h1}</h1>
          <p className="text-base md:text-lg text-[#8A8A9B] mt-6 leading-relaxed">{p.intro}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href="#quote" className="btn-primary px-7 py-3.5 text-sm" data-testid="product-quote-cta">Get a Hologram Sticker Quote</a>
            <a href={SITE.whatsapp} target="_blank" rel="noreferrer" className="btn-ghost px-7 py-3.5 text-sm">WhatsApp Us</a>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="card overflow-hidden">
            <img src={p.image} alt={p.alt} className="w-full aspect-square object-cover" loading="eager" />
          </div>
        </Reveal>
      </section>

      <section className="px-6 md:px-10 lg:px-16 py-16 border-t border-white/10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Block label="Product Benefits" items={p.benefits} />
        <Block label="Security Features" items={p.security} />
        <Block label="Customization Options" items={p.customization} />
        <Block label="Materials" items={p.materials} />
        <Block label="Available Shapes" items={p.shapes} />
        <Block label="Available Sizes" items={p.sizes} />
        <Block label="Printing / Security Options" items={p.printing} />
        <Block label="Applications" items={p.applications} />
        <div className="card p-6">
          <Overline>Why Choose NanoMax</Overline>
          <p className="text-sm text-[#C9C9D4] mt-4 leading-relaxed">UAE-based in Sharjah Media City, NanoMax customizes {p.name.toLowerCase()} around your brand with flexible sizes, security features and clear communication from artwork to delivery.</p>
        </div>
      </section>

      <section className="px-6 md:px-10 lg:px-16 py-16 border-t border-white/10">
        <Overline>Production Process</Overline>
        <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-4 mt-8">
          {["Requirement", "Artwork", "Security Features", "Approval", "Production", "Delivery"].map((s, i) => (
            <div key={s} className="card p-5">
              <div className="font-mono2 holo-text text-2xl font-bold">0{i + 1}</div>
              <div className="text-sm text-[#C9C9D4] mt-2">{s}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-10 lg:px-16 py-16 border-t border-white/10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4"><Overline>FAQ</Overline><h2 className="font-head text-2xl md:text-3xl font-bold mt-4">Questions about {p.name}.</h2></div>
        <div className="lg:col-span-8">
          <Accordion type="single" collapsible>
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`f${i}`} className="border-white/10">
                <AccordionTrigger className="text-left font-head hover:text-holo-cyan hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#8A8A9B] text-sm leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <div id="quote"><CtaBand sourcePage={path} /></div>

      <RelatedGrid title="Related Products" items={PRODUCTS.filter((x) => x.slug !== p.slug).slice(0, 4).map((x) => ({ name: x.name, desc: x.tile, to: `/products/${x.slug}` }))} />
    </>
  );
}
